import { apiService } from './apiService';
import { DiaryRecord } from '../models/diaryRecord';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface DiaryRecordFilters {
  date?: string; // DD-MM-YYYY (single day)
  date_from?: string; // DD-MM-YYYY (closed range start)
  date_to?: string; // DD-MM-YYYY (closed range end)
  day_of_week?: string;
  diary_item?: string | number;
}

// CustomPagination envelope, same shape as the diary-items response.
interface DiaryRecordsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: DiaryRecord[];
}

const PER_PAGE = 100;

const buildUrl = (patientId: string | number, filters: DiaryRecordFilters, page: number): string => {
  const params = new URLSearchParams();
  if (filters.date) params.append('date', filters.date);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.day_of_week) params.append('day_of_week', filters.day_of_week);
  if (filters.diary_item !== undefined) params.append('diary_item', String(filters.diary_item));
  params.append('page', String(page));
  params.append('perpage', String(PER_PAGE));
  return `${API_BASE_URL}/clinic/patient/${patientId}/diary-records?${params.toString()}`;
};

export const diaryRecordsService = {
  // Fetch all logged records for a patient matching the given filters (handles pagination).
  getDiaryRecords: async (
    patientId: string | number,
    filters: DiaryRecordFilters = {}
  ): Promise<DiaryRecord[]> => {
    try {
      const firstPage: DiaryRecordsResponse = await apiService.get(buildUrl(patientId, filters, 1));
      const results = [...firstPage.results];

      const totalPages = Math.ceil(firstPage.count / PER_PAGE);
      if (totalPages <= 1) return results;

      const remaining = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          apiService.get(buildUrl(patientId, filters, i + 2)) as Promise<DiaryRecordsResponse>
        )
      );
      return results.concat(...remaining.map(page => page.results));
    } catch (error) {
      console.error(`Error fetching diary records for patient ${patientId}:`, error);
      throw error;
    }
  },
};
